/** venture/BankGuideSteps.tsx — 은행 구비서류 안내장의 「금융 업무 진행 순서」 편집 (5단계 · 2026-10-31)
 *  지도사님 「내가 편집하는 기능 — 벤처투자확인서처럼 · 디폴트는 내가 시킨 대로」
 *  · 처음 열면 등록 정보(GP · LP 출자금 · 법인 · 예시 LP)로 만든 기본 순서를 그대로 보여 준다(서버 defaultBankSteps — 안내장과 같은 규칙)
 *  · 고치고 「저장하고 다시 만들기」 → s5.txt.bankguide_form 에 저장 + 안내장 재생성 · 1단계(계좌 개설 · 지금 단계)는 고정
 *  · 「기본값으로」 → 저장값을 지우고 등록 정보 기준으로 다시 */
import React, { useState } from 'react';
import { bankguideSteps } from './api';
import type { BankStep } from './api';
import { regenDocs } from './docgen';
import { getTxt, mutStore } from './stepcommon';
import type { Master, Project } from './types';

interface Props { p: Project; onPatch: (patch: Partial<Project>) => void; toast: (m: string) => void }

const MAX = 10;
const box: React.CSSProperties = { background: '#F8FAFC', border: '1px solid #E4E7EF', borderRadius: 8, padding: 12, margin: '0 0 8px' };
const num = (bg: string, fg: string): React.CSSProperties => ({ width: 26, height: 26, flexShrink: 0, borderRadius: 6, background: bg, color: fg, fontSize: 12, fontWeight: 700, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' });
const line: React.CSSProperties = { display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 };
const ico: React.CSSProperties = { width: 28, padding: '5px 0', textAlign: 'center' };

function savedSteps(p: Project): BankStep[] | null {
  try {
    const f = JSON.parse(getTxt(p, 's5', 'bankguide_form') || 'null') as { steps?: BankStep[] } | null;
    return f && Array.isArray(f.steps) && f.steps.length ? f.steps : null;
  } catch { return null; }
}

export default function BankGuideSteps({ p, onPatch, toast }: Props) {
  const edited = savedSteps(p);
  const [open, setOpen] = useState(false);
  const [rows, setRows] = useState<BankStep[] | null>(null);
  const [busy, setBusy] = useState('');

  const loadDefault = async (): Promise<BankStep[]> => {
    const master = p.master as Master | undefined;
    if (!master) throw new Error('마스터 정보가 없습니다 — 3단계에서 먼저 입력하세요');
    const { steps } = await bankguideSteps(p.id, master, p.cos || []);
    return steps;
  };
  const toggle = async () => {
    if (open) { setOpen(false); return; }
    setOpen(true);
    if (edited) { setRows(edited.map((r) => ({ ...r }))); return; }
    setBusy('load');
    try { setRows(await loadDefault()); } catch (e) { toast((e as Error).message); setOpen(false); } finally { setBusy(''); }
  };
  const set = (i: number, patch: Partial<BankStep>) => setRows((rs) => (rs || []).map((r, j) => (j === i ? { ...r, ...patch } : r)));
  const move = (i: number, d: -1 | 1) => setRows((rs) => {
    const a = (rs || []).slice(); const j = i + d;
    if (j < 0 || j >= a.length) return a;
    [a[i], a[j]] = [a[j], a[i]]; return a;
  });
  const del = (i: number) => setRows((rs) => (rs || []).filter((_, j) => j !== i));
  const add = () => setRows((rs) => ((rs || []).length >= MAX ? rs : [...(rs || []), { title: '', desc: '' }]));

  const regen = async (patch: Partial<Project>) => {
    onPatch(patch);
    await regenDocs({ ...p, ...patch } as Project, ['bankguide'], onPatch, toast);
  };
  const save = async () => {
    const steps = (rows || []).map((r) => ({ title: r.title.trim(), desc: r.desc.trim() })).filter((r) => r.title);
    if (!steps.length) { toast('단계를 하나 이상 적어 주세요'); return; }
    setBusy('save');
    try { await regen(mutStore(p, 's5', (st) => { st.txt = { ...(st.txt || {}), bankguide_form: JSON.stringify({ steps }) }; })); setOpen(false); }
    catch { /* regenDocs 가 알림 */ } finally { setBusy(''); }
  };
  const reset = async () => {
    setBusy('reset');
    try {
      const patch = mutStore(p, 's5', (st) => { const t = { ...(st.txt || {}) }; delete t.bankguide_form; st.txt = t; });
      setRows(await loadDefault());
      await regen(patch);
      toast('등록 정보 기준 기본 순서로 되돌렸어요');
    } catch (e) { if ((e as Error).message !== 'no master') toast((e as Error).message); } finally { setBusy(''); }
  };

  return (
    <>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, margin: '2px 0 8px' }}>
        <span style={{ fontSize: 12, color: 'var(--muted, #6B7280)' }}>금융 업무 진행 순서</span>
        <span className={'pill ' + (edited ? 'p-blue' : 'p-gray')}>{edited ? '직접 고친 순서' : '등록 정보로 자동'}</span>
        <span style={{ flex: 1 }} />
        <button type="button" className="rbtn" disabled={busy === 'load'} onClick={() => void toggle()}>{busy === 'load' ? '불러오는 중…' : '✎ 진행 순서 편집 ' + (open ? '▲' : '▼')}</button>
      </div>
      {open && rows && (
        <div style={box}>
          <div style={line}>
            <span style={num('#F37321', '#fff')}>1</span>
            <input className="cotext" style={{ flex: 2 }} value="조합명의 계좌 개설" disabled />
            <input className="cotext" style={{ flex: 3 }} value="지금 단계 — 안내장 첫 줄은 늘 그대로" disabled />
            <span style={{ width: 90, flexShrink: 0 }} />
          </div>
          {rows.map((r, i) => (
            <div key={i} style={line}>
              <span style={num('#ECE8E4', '#2B2724')}>{i + 2}</span>
              <input className="cotext" style={{ flex: 2 }} value={r.title} onChange={(e) => set(i, { title: e.target.value })} placeholder="단계 제목 — 예: 업무집행조합원 출자금 5,000만원 입금" aria-label={(i + 2) + '단계 제목'} />
              <input className="cotext" style={{ flex: 3 }} value={r.desc} onChange={(e) => set(i, { desc: e.target.value })} placeholder="설명(없어도 됨) — 예: 윤명원 → 조합 통장" aria-label={(i + 2) + '단계 설명'} />
              <span style={{ display: 'inline-flex', gap: 3, width: 90, flexShrink: 0 }}>
                <button type="button" className="rbtn" style={ico} disabled={i === 0} onClick={() => move(i, -1)} aria-label="위로">↑</button>
                <button type="button" className="rbtn" style={ico} disabled={i === rows.length - 1} onClick={() => move(i, 1)} aria-label="아래로">↓</button>
                <button type="button" className="rbtn" style={ico} onClick={() => del(i)} aria-label="빼기">✕</button>
              </span>
            </div>
          ))}
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 10 }}>
            <button type="button" className="rbtn" disabled={rows.length >= MAX} onClick={add}>＋ 단계 추가</button>
            <span style={{ fontSize: 11, color: '#94A3B8' }}>제목이 빈 줄은 빼고 만들어요</span>
            <span style={{ flex: 1 }} />
            <button type="button" className="rbtn" style={{ minWidth: 132, height: 32 }} disabled={!!busy} onClick={() => void reset()}>{busy === 'reset' ? '되돌리는 중…' : '기본값으로 되돌리기'}</button>
            <button type="button" className="btn pri sm" style={{ minWidth: 132, height: 32 }} disabled={!!busy} onClick={() => void save()}>{busy === 'save' ? '만드는 중…' : '저장하고 다시 만들기'}</button>
          </div>
        </div>
      )}
    </>
  );
}
